import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Sparkles } from 'lucide-react';
import { BUSINESS_INFO } from '../data';
import { useLang } from '../context/LangContext';

function useCountUp(target, duration = 1400, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const numeric = parseInt(target.replace(/\D/g, ''), 10);
    if (isNaN(numeric)) { setCount(target); return; }
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * numeric));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(numeric);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

export default function Hero() {
  const { t } = useLang();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setStatsVisible(true); obs.unobserve(el); }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const count500 = useCountUp('500', 1600, statsVisible);

  const scrollTo = (id, e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const images = [
    {
      style: { gridRow: '1/3' },
      img: 'https://images.pexels.com/photos/36747289/pexels-photo-36747289/free-photo-of-elegant-woman-in-red-evening-gown-on-dark-background.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500',
      hint: 'Party Dresses',
    },
    {
      img: 'https://images.pexels.com/photos/27253986/pexels-photo-27253986/free-photo-of-punjabi-boy.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500',
      hint: 'Panjabi & Koti',
    },
    {
      img: 'https://images.pexels.com/photos/5246793/pexels-photo-5246793.jpeg',
      hint: 'Henna Mehndi',
    },
  ];

  const stats = [
    { num: `${count500}+`, raw: '500+', label: t.designs },
    { num: 'IN & PK',       raw: 'IN & PK', label: t.origin },
    { num: 'Venice',        raw: 'Venice',  label: t.location },
  ];

  return (
    <section id="top" className="hero-wrapper">
      {/* ── Left Content — Depth 4 (UI/Text Layer) ── */}
      <div className="hero-content depth-4">
        {/* Badge */}
        <div className="badge animate-up delay-1" style={{ marginBottom: '2rem', width: 'fit-content' }}>
          <Sparkles size={11} />
          {t.badge}
        </div>

        {/* Headline — cinematic split entrance */}
        <h1
          className="heading-display animate-left delay-2 w-full text-center md:text-left"
          style={{ marginBottom: '1.5rem', maxWidth: '560px' }}
        >
          {t.heroTitle1}
          <br className="hidden md:block" />
          {t.heroTitle2}{' '}
          <em style={{
            fontStyle: 'italic', fontWeight: 300,
            color: 'var(--accent)',
          }}>
            {t.heroTitle3}
          </em>
        </h1>

        <p className="heading-sub animate-up delay-3" style={{ marginBottom: '0.75rem' }}>
          {t.heroSub}
        </p>
        <p className="text-body animate-up delay-3" style={{ maxWidth: '400px', marginBottom: '2.5rem' }}>
          {t.heroDesc}
        </p>

        {/* CTAs */}
        <div className="animate-up delay-4 flex flex-col sm:flex-row w-full sm:w-auto items-center justify-center md:justify-start gap-4">
          <a
            href="#collections"
            onClick={e => scrollTo('collections', e)}
            className="btn-primary w-full sm:w-auto justify-center"
          >
            {t.explore} <ArrowDown size={14} />
          </a>
          <a
            href="#booking"
            onClick={e => scrollTo('booking', e)}
            className="btn-secondary w-full sm:w-auto justify-center"
          >
            {t.bookVisit || 'Book a Visit'}
          </a>
        </div>

        {/* Stats counter */}
        <div
          ref={statsRef}
          className="animate-up delay-5 w-full flex flex-row flex-wrap justify-center md:justify-start"
          style={{ gap: 'clamp(1.5rem,4vw,3rem)', marginTop: '3rem' }}
        >
          {stats.map((s, i) => (
            <div key={s.label} className="flex flex-row items-center" style={{ gap: 'clamp(1.5rem,4vw,3rem)' }}>
              {i > 0 && <div className="hidden md:block" style={{ width: '1px', height: '100%', background: 'var(--border)' }} />}
              <div className="flex flex-col items-center md:items-start text-center md:text-left">
                <div style={{
                  fontFamily: 'var(--font-serif)', fontSize: '1.85rem', fontWeight: 500,
                  color: i === 0 ? 'var(--accent)' : 'var(--text)', lineHeight: 1,
                }}>
                  {s.num}
                </div>
                <div className="heading-sub" style={{ fontSize: '0.62rem', marginTop: '0.35rem' }}>
                  {s.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right Visual — Depth 3 (Main Objects) ── */}
      <div className="hero-visual reveal depth-3">
        <div className="hero-visual-grid">
          {images.map((cell, i) => (
            <div
              key={i}
              className="img-overlay"
              style={{
                ...(cell.style || {}),
                borderRadius: i === 0 ? '24px 24px 8px 24px' : i === 1 ? '24px 8px 8px 8px' : '8px 8px 24px 8px',
                transition: 'transform 0.5s cubic-bezier(0.25,1,0.5,1)',
              }}
            >
              <img
                src={cell.img}
                alt={cell.hint}
                style={{
                  animationDelay: `${i * 1.5}s`,
                  animation: i === 0 ? 'float 8s ease-in-out infinite' : `float ${9 + i}s ease-in-out infinite`,
                  willChange: 'transform',
                }}
              />
              {/* Depth label overlay */}
              <div style={{
                position: 'absolute', bottom: '1rem', left: '1rem', zIndex: 3,
              }}>
                <span className="badge" style={{
                  color: '#fff',
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(10px)',
                }}>
                  {cell.hint}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Floating glass disclaimer */}
        <div
          className="glass"
          style={{
            position: 'absolute', bottom: '0.75rem', left: '50%',
            transform: 'translateX(-50%)',
            padding: '0.5rem 1rem',
            borderRadius: '50px',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            fontSize: '0.65rem', color: 'var(--text-muted)',
            zIndex: 10, whiteSpace: 'nowrap',
          }}
        >
          <span>📷</span> {t.disclaimer}{' '}
          <a
            href={BUSINESS_INFO.facebook}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)', fontWeight: 700 }}
          >
            {t.disclaimerLink}
          </a>
        </div>
      </div>
    </section>
  );
}
